/**
 * Handle WooCommerce added_to_cart jQuery event.
 *
 * @since 1.0.0
 */

import { popupManager } from '@/frontend/components';
import { extractCartInfoFromFragments } from '@/frontend/transformers/cart-transformer';
import {
  transformJustAddedItem,
  transformPhpProduct,
} from '@/frontend/transformers/product-transformer';
import type { RedFoxCartData } from '@/frontend/types/cart';

import type { RedfoxPopupData } from '../types';

/**
 * Handle WooCommerce added_to_cart jQuery event.
 * Uses the existing redfox_cart_popup_data fragment from PHP.
 *
 * @since 1.0.0
 *
 * @param fragments WooCommerce fragments containing redfox_cart_popup_data.
 * @param _cartHash Cart hash.
 *
 * @return void
 */
export const handleWooCommerceAddedToCartEvent = (
  fragments: Record<string, string>,
  _cartHash: string
): void => {
  try {
    // Look for our custom fragment data.
    const redfoxDataJson = fragments['redfox_cart_popup_data'];

    if (redfoxDataJson) {
      const redfoxData = JSON.parse(redfoxDataJson) as RedfoxPopupData;

      // Use popup_data from your PHP implementation.
      const popupData = redfoxData.popup_data;

      if (popupData?.products && popupData.products.length > 0) {
        // Transform your PHP data to our frontend format.
        const cartData: RedFoxCartData = {
          message: popupData.message || 'Product added to cart',
          products: popupData.products.map(transformPhpProduct),
          just_added_products: popupData.just_added_products?.map(transformPhpProduct) || [],
          total_quantity: popupData.total_quantity || 0,
          total_price: popupData.total_price || 0,
          formatted_total_price: popupData.formatted_total_price || '',
          show_quantity: popupData.show_quantity !== false,
          cart_info: (popupData.cart_info as RedFoxCartData['cart_info']) || {
            count: 0,
            cart_url: '/cart',
            checkout_url: '/checkout',
          },
          timestamp: popupData.timestamp || Date.now(),
        };

        // Use the first just added product as primary, fallback to first product in cart
        const primaryProduct = cartData.just_added_products?.[0] || cartData.products?.[0];
        if (primaryProduct) {
          popupManager.showCart(primaryProduct, cartData, 'wc-cart-added');
        }
      } else if (redfoxData.just_added_items) {
        // Fallback: use just_added_items data.
        const justAddedItems = Object.values(redfoxData.just_added_items) as Array<
          Record<string, unknown>
        >;

        if (justAddedItems.length > 0) {
          const firstItem = justAddedItems[0];
          if (firstItem) {
            const product = transformJustAddedItem(firstItem, redfoxData.cart_fragments);

            const fallbackCartData: RedFoxCartData = {
              message: 'Product added to cart',
              products: [product],
              total_quantity: Number(firstItem['quantity_added']) || 1,
              total_price: product.sale_price * product.quantity,
              formatted_total_price: product.formatted_price,
              show_quantity: true,
              cart_info: extractCartInfoFromFragments(redfoxData.cart_fragments || {}),
              timestamp: Number(firstItem['timestamp']) || Date.now(),
            };

            popupManager.showCart(product, fallbackCartData, 'wc-cart-added');
          }
        }
      }
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error handling WooCommerce added_to_cart event:', error);
  }
};
